/**
 * Desktop catalog mega menu + mobile drawer behavior for catalog-navigation-header section.
 */

const CATALOG_NAV_FIXED_TOP_VAR = '--catalog-nav-fixed-top';

let catalogNavFixedTopRaf = 0;

/** @type {ResizeObserver | null} */
let catalogNavHeaderResizeObserver = null;

let catalogNavFixedTopListenersBound = false;

function updateCatalogNavFixedTop() {
  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) return;

  document.body.style.setProperty(
    CATALOG_NAV_FIXED_TOP_VAR,
    `${Math.round(header.getBoundingClientRect().bottom)}px`
  );
}

function scheduleCatalogNavFixedTopUpdate() {
  if (catalogNavFixedTopRaf) return;
  catalogNavFixedTopRaf = requestAnimationFrame(() => {
    catalogNavFixedTopRaf = 0;
    updateCatalogNavFixedTop();
  });
}

function initCatalogNavFixedTopSync() {
  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) return;

  if (!catalogNavFixedTopListenersBound) {
    catalogNavFixedTopListenersBound = true;
    document.addEventListener('scroll', scheduleCatalogNavFixedTopUpdate, { passive: true });
    window.addEventListener('resize', scheduleCatalogNavFixedTopUpdate);
  }

  catalogNavHeaderResizeObserver?.disconnect();
  catalogNavHeaderResizeObserver = new ResizeObserver(() => scheduleCatalogNavFixedTopUpdate());
  catalogNavHeaderResizeObserver.observe(header);

  updateCatalogNavFixedTop();
}

class CatalogNavMegamenu extends HTMLElement {
  constructor() {
    super();
    this.openMegaMenu = this.openMegaMenu.bind(this);
    this.closeMegaMenu = this.closeMegaMenu.bind(this);
    this.maybeCloseMegaMenu = this.maybeCloseMegaMenu.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.isOverOpener = false;
    this.isOverMenu = false;
    this.closeDelayMs = 120;
    this.closeTimer = null;
    this.openTab = null;

    this.scrollCloseThreshold = 12;
    this._lastScrollY = window.scrollY || 0;
  }

  getSectionRoot() {
    return (
      this.closest('.catalog-navigation-header') ||
      this.closest('[data-catalog-navigation-root]')
    );
  }

  connectedCallback() {
    const root = this.getSectionRoot();
    if (!root) return;

    this.sectionRoot = root;
    this.overLay = root.querySelector('[data-catalog-nav-overlay]');
    this.menuOpeners = root.querySelectorAll(
      '.catalog-nav-level-one[data-haschildren]'
    );

    this.menuTabs = Array.from(
      this.querySelectorAll('[data-catalog-nav-megamenu-tab]')
    );
    this.hoverArea = root.querySelector('.catalog-nav-link-hover-area');

    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', 'Site menu');
    this.setAttribute('aria-hidden', 'true');

    this.attachEvents();

    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('catalog-nav-megamenu--open');
  }

  getContentHeight(element) {
    if (!(element instanceof HTMLElement)) {
      return 0;
    }
    return element.scrollHeight + 48;
  }

  disconnectedCallback() {
    this.detachEvents();
    if (this.closeTimer) clearTimeout(this.closeTimer);
  }

  openMegaMenu(tab) {
    scheduleCatalogNavFixedTopUpdate();
    if (!tab || !this.overLay) return;
    this.changeTab(tab);
    this.classList.add('catalog-nav-megamenu--open');
    this.overLay.classList.add('open');
    this.setAttribute('aria-hidden', 'false');
  }

  closeMegaMenu() {
    if (this.overLay) {
      this.overLay.classList.remove('open');
    }
    this.changeTab();
    this.classList.remove('catalog-nav-megamenu--open');
    this.setAttribute('aria-hidden', 'true');
  }

  maybeCloseMegaMenu() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (!this.isOverOpener && !this.isOverMenu) {
        this.closeMegaMenu();
      }
    }, this.closeDelayMs);
  }

  onScroll() {
    const y = window.scrollY || 0;
    if (this.classList.contains('catalog-nav-megamenu--open')) {
      if (Math.abs(y - this._lastScrollY) >= this.scrollCloseThreshold) {
        this.isOverOpener = false;
        this.isOverMenu = false;
        this.closeMegaMenu();
      }
    }
    this._lastScrollY = y;
  }

  changeTab(tab = null) {
    if (this.openTab) this.openTab.classList.remove('open');
    if (!tab) {
      this.style.height = '';
      this.openTab = null;
      return;
    }
    const heightEl = tab.querySelector('[data-megamenu-height]');
    this.style.height = `${this.getContentHeight(heightEl)}px`;
    tab.classList.add('open');
    this.openTab = tab;
  }

  attachEvents() {
    this._openerHandlers = [];
    this.menuOpeners.forEach((opener) => {
      const key = opener.dataset.key;
      const tab =
        this.menuTabs.filter((t) => t.dataset.key === key)[0] ?? null;
      const onEnter = () => {
        if (!tab) return;
        this.isOverOpener = true;
        this.openMegaMenu(tab);
      };
      const onLeave = () => {
        this.isOverOpener = false;
        this.maybeCloseMegaMenu();
      };
      opener.addEventListener('mouseenter', onEnter);
      opener.addEventListener('mouseleave', onLeave);
      opener.addEventListener('focusin', onEnter);
      opener.addEventListener('focusout', onLeave);
      this._openerHandlers.push([opener, onEnter, onLeave]);
    });

    this._onMenuEnter = () => {
      this.isOverMenu = true;
    };
    this._onMenuLeave = () => {
      this.isOverMenu = false;
      this.maybeCloseMegaMenu();
    };
    this.addEventListener('mouseenter', this._onMenuEnter);
    this.addEventListener('mouseleave', this._onMenuLeave);

    if (this.hoverArea) {
      this._onHoverEnter = () => {
        this.isOverOpener = true;
      };
      this._onHoverLeave = () => {
        this.isOverOpener = false;
        this.maybeCloseMegaMenu();
      };
      this.hoverArea.addEventListener('mouseenter', this._onHoverEnter);
      this.hoverArea.addEventListener('mouseleave', this._onHoverLeave);
    }

    if (this.overLay) {
      this._onOverlayClick = () => {
        this.isOverOpener = false;
        this.isOverMenu = false;
        this.closeMegaMenu();
      };
      this.overLay.addEventListener('click', this._onOverlayClick);
    }

    this._onScroll = this.onScroll;
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  detachEvents() {
    if (this._openerHandlers) {
      this._openerHandlers.forEach(([opener, onEnter, onLeave]) => {
        opener.removeEventListener('mouseenter', onEnter);
        opener.removeEventListener('mouseleave', onLeave);
        opener.removeEventListener('focusin', onEnter);
        opener.removeEventListener('focusout', onLeave);
      });
      this._openerHandlers = null;
    }

    if (this._onMenuEnter) {
      this.removeEventListener('mouseenter', this._onMenuEnter);
    }
    if (this._onMenuLeave) {
      this.removeEventListener('mouseleave', this._onMenuLeave);
    }

    if (this.hoverArea && this._onHoverEnter && this._onHoverLeave) {
      this.hoverArea.removeEventListener('mouseenter', this._onHoverEnter);
      this.hoverArea.removeEventListener('mouseleave', this._onHoverLeave);
    }

    if (this.overLay && this._onOverlayClick) {
      this.overLay.removeEventListener('click', this._onOverlayClick);
    }

    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      this._onScroll = null;
    }
  }
}

if (!customElements.get('catalog-nav-megamenu')) {
  customElements.define('catalog-nav-megamenu', CatalogNavMegamenu);
}

class CatalogNavMobileMegamenu extends HTMLElement {
  constructor() {
    super();
    this.open = false;
    this.currentOpenLevelTwoKey = null;
  }

  /** @type {AbortController | null} */
  #abortController = null;

  /**
   * @param {KeyboardEvent} event
   */
  #handleEscape = (event) => {
    if (!this.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.closeMegamenu();
  };

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const root = this.closest('.catalog-navigation-header') || this.closest('[data-catalog-navigation-root]');
    this.sectionRoot = root;
    this.opener = root?.querySelector('[data-catalog-nav-mobile-opener]');
    this.overlay = this.closest('.catalog-nav-mobile-megamenu-wrapper');
    this.levelOneLi = this.querySelectorAll('.catalog-nav-mobile-megamenu-first-level');
    this.levelTwoElements = Array.from(this.querySelectorAll('.catalog-nav-menu-level-2'));
    this.levelTwoClosers = this.querySelectorAll('.catalog-nav-menu-level-2-closer');
    this.letterOpeners = Array.from(this.querySelectorAll('.catalog-nav-letter-brands-title'));
    this.attachEvents(signal);
    this.#syncA11y(false);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    document.removeEventListener('keydown', this.#handleEscape);
    if (this.open) {
      document.body.classList.remove('disable-body-scroll');
      this.overlay?.classList.remove('open');
      this.open = false;
    }
    if (this.opener instanceof HTMLElement) {
      this.opener.setAttribute('aria-expanded', 'false');
    }
    if (this.overlay instanceof HTMLElement) {
      this.overlay.setAttribute('aria-hidden', 'true');
    }
  }

  openMegamenu() {
    document.removeEventListener('keydown', this.#handleEscape);
    scheduleCatalogNavFixedTopUpdate();
    document.body.classList.add('disable-body-scroll');
    this.overlay?.classList.add('open');
    this.open = true;
    this.#syncA11y(true);
    document.addEventListener('keydown', this.#handleEscape);
  }

  closeMegamenu() {
    document.body.classList.remove('disable-body-scroll');
    this.overlay?.classList.remove('open');
    this.closeLevelTwo();
    this.closeAllLetters();
    this.open = false;
    this.#syncA11y(false);
    document.removeEventListener('keydown', this.#handleEscape);
  }

  /**
   * @param {boolean} expanded
   */
  #syncA11y(expanded) {
    if (this.opener instanceof HTMLElement) {
      this.opener.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
    if (this.overlay instanceof HTMLElement) {
      this.overlay.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    }
  }

  openLevelTwo(key) {
    const panel = this.levelTwoElements.filter((el) => el.dataset.key === key)[0];
    if (panel) {
      panel.classList.add('open');
      this.currentOpenLevelTwoKey = key;
    }
  }

  closeLevelTwo() {
    if (!this.currentOpenLevelTwoKey) return;
    const panel = this.levelTwoElements.filter(
      (el) => el.dataset.key === this.currentOpenLevelTwoKey
    )[0];
    panel?.classList.remove('open');
    this.currentOpenLevelTwoKey = null;
  }

  closeAllLetters() {
    this.letterOpeners.forEach((letterOpener) => {
      letterOpener.classList.remove('open');
      const chev = letterOpener.querySelector('.catalog-nav-letter-brands-chevron');
      chev?.classList.remove('open');
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  attachEvents(signal) {
    this.opener?.addEventListener(
      'click',
      () => {
        if (this.open) {
          this.closeMegamenu();
        } else {
          this.openMegamenu();
        }
      },
      { signal }
    );

    this.overlay?.addEventListener(
      'click',
      (event) => {
        if (event.target === event.currentTarget) {
          this.closeMegamenu();
        }
      },
      { signal }
    );

    this.levelOneLi.forEach((row) => {
      row.addEventListener(
        'click',
        (event) => {
          const hitOpener = event.target.closest('.catalog-nav-mobile-megamenu-first-level-opener');
          if (row.classList.contains('hasChildren') && hitOpener) {
            event.preventDefault();
            this.openLevelTwo(row.dataset.key || '');
          }
        },
        { signal }
      );
    });

    this.levelTwoClosers.forEach((closer) => {
      closer.addEventListener(
        'click',
        () => {
          this.closeLevelTwo();
          this.closeAllLetters();
        },
        { signal }
      );
    });

    this.letterOpeners.forEach((opener) => {
      const svg = opener.querySelector('.catalog-nav-letter-brands-chevron');
      opener.addEventListener(
        'click',
        (event) => {
          if (event.target instanceof Element && event.target.closest('a')) {
            return;
          }
          event.preventDefault();
          event.currentTarget.classList.toggle('open');
          svg?.classList.toggle('open');
        },
        { signal }
      );
    });
  }
}

if (!customElements.get('catalog-nav-mobile-megamenu')) {
  customElements.define('catalog-nav-mobile-megamenu', CatalogNavMobileMegamenu);
}

function initBrandAlphabetMega(root) {
  if (!(root instanceof HTMLElement) || root.dataset.catalogNavBrandsBound) return;

  const pages = root.querySelectorAll('.catalog-nav-brand-page');
  const letters = root.querySelectorAll('.catalog-nav-megamenu-alphabet-item');
  if (!pages.length || !letters.length) return;

  root.dataset.catalogNavBrandsBound = 'true';

  let selectedLetter =
    [...letters].find((letter) => letter.classList.contains('selected')) ??
    letters[0];

  letters.forEach((letter) => {
    letter.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const dataLetter = target.dataset.letter;
      if (!dataLetter || !selectedLetter) return;
      selectedLetter.classList.remove('selected');
      target.classList.add('selected');
      selectedLetter = target;
      pages.forEach((page) => {
        if (page.dataset.letter === dataLetter) {
          page.classList.add('visible');
        } else {
          page.classList.remove('visible');
        }
      });
    });
  });
}

/**
 * @param {Document | HTMLElement | undefined | null} [root]
 */
function initBrandAlphabetPanels(root) {
  const scope = root ?? document;
  scope.querySelectorAll('[data-catalog-nav-brands-megamenu]').forEach((el) => {
    initBrandAlphabetMega(el);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initBrandAlphabetPanels();
  initCatalogNavFixedTopSync();
});

if (document.readyState !== 'loading') {
  initBrandAlphabetPanels();
  initCatalogNavFixedTopSync();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement|null} */ (event.target);
  initBrandAlphabetPanels(target ?? document);
  queueMicrotask(() => initCatalogNavFixedTopSync());
});
